import { Link } from '@tanstack/react-router';
import { UserFullname } from '@r/components/user-fullname.tsx';
import { Avatar, AvatarFallback } from '@ui/avatar.tsx';
import { Button } from '@ui/button.tsx';
import { Card, CardContent, CardHeader, CardTitle } from '@ui/card.tsx';
import { ComboboxInput } from '@ui/combobox-input.tsx';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@ui/dialog.tsx';
import { Input } from '@ui/input.tsx';
import { Label } from '@ui/label.tsx';
import { Textarea } from '@ui/textarea.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@r/components/ui/select.tsx';
import {
  Building2,
  CircleAlert,
  Contact,
  ExternalLink,
  Loader2,
  Pencil,
  Users
} from 'lucide-react';
import type { Option } from '@r/types/index.ts';
import { useTranslation } from 'react-i18next';

type CompanyOption = Option;
type CompanyResponsible = { id?: string; fullname: string; isDeleted?: boolean };

type UserDetailsGridProps = {
  user: { firstname?: string; lastname?: string; email?: string };
  workspace?: {
    uniqueCode?: string;
    maxRole?: string;
    createdAt?: string;
    phone?: string | null;
    comment?: string | null;
    comment2?: string | null;
    comment3?: string | null;
    status?: string;
    company?: { id?: number; name?: string; uniqueCode?: string } | null;
  };
  formattedCreatedAt: string;
  formattedRegisteredAt: string;
  roleLabel: string;
  termsLabel: string;
  modalState: {
    isAboutOpen: boolean;
    isCompanyOpen: boolean;
  };
  drafts: {
    firstname: string;
    lastname: string;
    personalEmail: string;
    personalPhone: string;
    comment: string;
    comment2: string;
    comment3: string;
    uniqueCode: string;
    programRole: string;
    programTermsAccepted: boolean;
    companySearch: string;
    selectedCompanyOption: CompanyOption | null;
  };
  canEditUniqueCode: boolean;
  assignableRoles: string[];
  isTermsReadOnly: boolean;
  loadingState: {
    updateAboutPending: boolean;
    updateCompanyPending: boolean;
    companyLoading: boolean;
    companyDetailsLoading: boolean;
  };
  companyOptions: CompanyOption[];
  companyId: string | null;
  companyResponsibles: CompanyResponsible[];
  handlers: {
    setIsAboutOpen: (open: boolean) => void;
    setIsCompanyOpen: (open: boolean) => void;
    onFirstnameChange: (value: string) => void;
    onLastnameChange: (value: string) => void;
    onPersonalEmailChange: (value: string) => void;
    onPersonalPhoneChange: (value: string) => void;
    onCommentChange: (value: string) => void;
    onComment2Change: (value: string) => void;
    onComment3Change: (value: string) => void;
    onUniqueCodeChange: (value: string) => void;
    onProgramRoleChange: (value: string) => void;
    onProgramTermsChange: (accepted: boolean) => void;
    onCompanySearchChange: (value: string) => void;
    onCompanyOptionChange: (option: CompanyOption | null) => void;
    onSubmitAboutModal: () => void;
    onSubmitCompanyModal: () => void;
  };
  translateRole: (role: string) => string;
  companySearchPlaceholder: string;
};

export function UserDetailsGrid({
  user,
  workspace,
  formattedCreatedAt,
  formattedRegisteredAt,
  roleLabel,
  termsLabel,
  modalState,
  drafts,
  canEditUniqueCode,
  assignableRoles,
  isTermsReadOnly,
  loadingState,
  companyOptions,
  companyId,
  companyResponsibles,
  handlers,
  translateRole,
  companySearchPlaceholder
}: UserDetailsGridProps) {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:items-stretch">
      <Card className="lg:col-span-2">
        <CardHeader className="p-6 pb-3">
          <div className="flex justify-between items-center">
            <CardTitle className="flex gap-2 items-center text-4xl font-bold">
              <Contact />
              <span className="text-xl font-bold">{t('admin.user.profile.section.about')}</span>
            </CardTitle>
            <Dialog open={modalState.isAboutOpen} onOpenChange={handlers.setIsAboutOpen}>
              <DialogTrigger asChild>
                <Button variant="link" className="px-0 h-auto text-primary">
                  <Pencil />
                  {t('common.edit')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto scrollable">
                <DialogHeader>
                  <DialogTitle>{t('admin.user.profile.modal.about.title')}</DialogTitle>
                </DialogHeader>

                <div className="py-2 space-y-4">
                  <p className="font-semibold">{t('admin.user.profile.section.personal.info')}</p>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-1">
                      <Label>
                        {t('label.user.firstname')} <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        value={drafts.firstname}
                        onChange={(event) => handlers.onFirstnameChange(event.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>
                        {t('label.user.lastname')} <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        value={drafts.lastname}
                        onChange={(event) => handlers.onLastnameChange(event.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>
                        {t('label.user.email')} <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        value={drafts.personalEmail}
                        onChange={(event) => handlers.onPersonalEmailChange(event.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>{t('label.user.phone')}</Label>
                      <Input
                        value={drafts.personalPhone}
                        onChange={(event) => handlers.onPersonalPhoneChange(event.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>{t('label.user.comment')}</Label>
                      <Textarea
                        value={drafts.comment}
                        onChange={(event) => handlers.onCommentChange(event.target.value)}
                        placeholder={t('user.comment.placeholder')}
                        maxLength={255}
                        rows={4}
                        className="resize-none"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>{t('label.user.comment2')}</Label>
                      <Textarea
                        value={drafts.comment2}
                        onChange={(event) => handlers.onComment2Change(event.target.value)}
                        placeholder={t('user.comment.placeholder')}
                        maxLength={255}
                        rows={4}
                        className="resize-none"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>{t('label.user.comment3')}</Label>
                      <Textarea
                        value={drafts.comment3}
                        onChange={(event) => handlers.onComment3Change(event.target.value)}
                        placeholder={t('user.comment.placeholder')}
                        maxLength={255}
                        rows={4}
                        className="resize-none"
                      />
                    </div>
                  </div>

                  <div className="h-px bg-border" />

                  <p className="font-semibold">{t('admin.user.profile.section.program.info')}</p>
                  <div className="grid grid-cols-1 gap-4">
                    {canEditUniqueCode ? (
                      <div className="space-y-1">
                        <Label>
                          {t('user.create.client.code.label')}{' '}
                          <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          value={drafts.uniqueCode}
                          onChange={(event) => handlers.onUniqueCodeChange(event.target.value)}
                          placeholder={t('user.create.client.code.placeholder')}
                        />
                      </div>
                    ) : null}
                    <div className="space-y-1">
                      <Label>
                        {t('label.user.role')} <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={drafts.programRole}
                        onValueChange={handlers.onProgramRoleChange}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {assignableRoles.map((role) => (
                            <SelectItem key={role} value={role}>
                              {translateRole(role)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label>{t('label.user.terms')}</Label>
                      <Select
                        value={drafts.programTermsAccepted ? 'accepted' : 'pending'}
                        onValueChange={(value) =>
                          handlers.onProgramTermsChange(value === 'accepted')
                        }
                        disabled={isTermsReadOnly}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="accepted">
                            {t('admin.user.profile.terms.accepted')}
                          </SelectItem>
                          <SelectItem value="pending">
                            {t('admin.user.profile.terms.pending')}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <DialogFooter>
                  <Button
                    variant="outline"
                    rounded="full"
                    disabled={loadingState.updateAboutPending}
                    onClick={() => handlers.setIsAboutOpen(false)}
                  >
                    {t('label.cancel')}
                  </Button>
                  <Button
                    rounded="full"
                    loading={loadingState.updateAboutPending}
                    onClick={handlers.onSubmitAboutModal}
                  >
                    {t('common.save')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent className="p-6 pt-0 space-y-8">
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <p className="font-semibold">{t('admin.user.profile.section.personal.info')}</p>
            </div>
            <div className="h-px bg-border" />
            <div className="grid grid-cols-[160px_minmax(0,1fr)] gap-y-4 text-base">
              <p className="text-sm font-medium text-muted-foreground">
                {t('label.user.firstname')}
              </p>
              <p className="text-sm">{user.firstname || '-'}</p>
              <p className="text-sm font-medium text-muted-foreground">
                {t('label.user.lastname')}
              </p>
              <p className="text-sm">{user.lastname || '-'}</p>
              <p className="text-sm font-medium text-muted-foreground">{t('label.user.email')}</p>
              <p className="text-sm">{user.email || '-'}</p>
              <p className="text-sm font-medium text-muted-foreground">{t('label.user.phone')}</p>
              <p className="text-sm">{workspace?.phone || '-'}</p>
              <p className="text-sm font-medium text-muted-foreground">{t('label.user.comment')}</p>
              <p className="min-w-0 text-sm whitespace-pre-wrap [overflow-wrap:anywhere]">
                {workspace?.comment || '-'}
              </p>
              <p className="text-sm font-medium text-muted-foreground">
                {t('label.user.comment2')}
              </p>
              <p className="min-w-0 text-sm whitespace-pre-wrap [overflow-wrap:anywhere]">
                {workspace?.comment2 || '-'}
              </p>
              <p className="text-sm font-medium text-muted-foreground">
                {t('label.user.comment3')}
              </p>
              <p className="min-w-0 text-sm whitespace-pre-wrap [overflow-wrap:anywhere]">
                {workspace?.comment3 || '-'}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <p className="font-semibold">{t('admin.user.profile.section.program.info')}</p>
            </div>
            <div className="h-px bg-border" />
            <div className="grid grid-cols-[160px_1fr] gap-y-4 text-base">
              <p className="text-sm font-medium text-muted-foreground">
                {t('label.user.unique.code')}
              </p>
              <p className="text-sm">{workspace?.uniqueCode || '-'}</p>
              <p className="text-sm font-medium text-muted-foreground">{t('label.user.role')}</p>
              <p className="text-sm">{roleLabel}</p>
              <p className="text-sm font-medium text-muted-foreground">
                {t('label.user.created.at')}
              </p>
              <p className="text-sm">{formattedCreatedAt}</p>
              <p className="text-sm font-medium text-muted-foreground">
                {t('label.user.registered.at')}
              </p>
              <p className="text-sm">{formattedRegisteredAt}</p>
              <p className="text-sm font-medium text-muted-foreground">{t('label.user.terms')}</p>
              <p className="text-sm">{termsLabel}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col gap-6 h-full min-h-0 lg:h-full">
        <Card className="flex flex-col flex-1">
          <CardHeader className="p-6 pb-3">
            <CardTitle className="flex gap-2 items-center text-4xl font-bold">
              <Building2 />
              <span className="text-xl font-bold">{t('label.user.company')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0 space-y-2">
            <div className="flex justify-between items-center">
              <p className="font-semibold">{t('label.user.company')}</p>
              <Dialog open={modalState.isCompanyOpen} onOpenChange={handlers.setIsCompanyOpen}>
                <DialogTrigger asChild>
                  <Button variant="link" className="px-0 h-auto text-primary">
                    <Pencil />
                    {t('common.edit')}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t('admin.user.profile.modal.company.title')}</DialogTitle>
                  </DialogHeader>

                  <Label>{t('label.user.company')}</Label>

                  <ComboboxInput
                    placeholder={companySearchPlaceholder}
                    immediate={true}
                    options={companyOptions}
                    input={drafts.companySearch}
                    onInputChange={handlers.onCompanySearchChange}
                    value={drafts.selectedCompanyOption}
                    onValueChange={handlers.onCompanyOptionChange}
                    loading={loadingState.companyLoading}
                  />

                  <DialogFooter>
                    <Button
                      variant="outline"
                      rounded="full"
                      disabled={loadingState.updateCompanyPending}
                      onClick={() => handlers.setIsCompanyOpen(false)}
                    >
                      {t('common.back')}
                    </Button>
                    <Button
                      rounded="full"
                      loading={loadingState.updateCompanyPending}
                      onClick={handlers.onSubmitCompanyModal}
                    >
                      {t('common.edit')}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <div className="h-px bg-border" />
            {workspace?.company ? (
              workspace.company.id ? (
                <Link
                  to="/companies/$id"
                  params={{ id: String(workspace.company.id) }}
                  className="flex justify-between items-center px-3 py-2 rounded-lg border transition-colors bg-background hover:bg-accent/40"
                >
                  <div className="flex gap-3 items-center">
                    <Avatar size="nav">
                      <AvatarFallback text={workspace.company.name} initials={true} />
                    </Avatar>
                    <div className="leading-tight">
                      <p className="text-sm font-semibold">{workspace.company.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {workspace.company.uniqueCode ?? ''}
                      </p>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-muted-foreground" />
                </Link>
              ) : (
                <div className="flex justify-between items-center px-3 py-2 rounded-lg border bg-background">
                  <div className="flex gap-3 items-center">
                    <Avatar size="nav">
                      <AvatarFallback text={workspace.company.name} initials={true} />
                    </Avatar>
                    <div className="leading-tight">
                      <p className="text-sm font-semibold">{workspace.company.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {workspace.company.uniqueCode ?? ''}
                      </p>
                    </div>
                  </div>
                </div>
              )
            ) : (
              <div className="flex flex-col gap-2 items-center">
                <span>
                  <CircleAlert className="text-primary" />
                </span>
                <p className="text-sm text-center text-muted-foreground">
                  {t('admin.user.profile.company.empty')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="flex flex-col flex-1">
          <CardHeader className="p-6 pb-3">
            <CardTitle className="flex gap-2 items-center text-4xl font-bold">
              <Users />
              <span className="text-xl font-bold">{t('admin.user.profile.section.contacts')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col flex-1 p-6 pt-0 space-y-2">
            <p className="font-semibold">{t('admin.user.profile.contacts.commercial')}</p>
            <div className="h-px bg-border" />
            {loadingState.companyDetailsLoading && companyId ? (
              <div className="flex flex-1 justify-center items-center">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            ) : companyResponsibles.length > 0 ? (
              <div className="space-y-2">
                {companyResponsibles.map((companyResponsible, index) => {
                  const responsibleKey =
                    companyResponsible.id ?? `${companyResponsible.fullname}-${index}`;
                  const content = (
                    <div key={responsibleKey} className="flex gap-3 items-center">
                      <Avatar size="nav">
                        <AvatarFallback text={companyResponsible.fullname} initials={true} />
                      </Avatar>
                      <div className="leading-tight">
                        <p className="text-sm font-semibold">
                          <UserFullname
                            fullname={companyResponsible.fullname}
                            isDeleted={companyResponsible.isDeleted}
                          />
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {t('admin.user.profile.contacts.commercial.responsible')}
                        </p>
                      </div>
                    </div>
                  );

                  return companyResponsible.id ? (
                    <Link
                      key={responsibleKey}
                      to="/users/$id"
                      params={{ id: companyResponsible.id }}
                      className="flex justify-between items-center px-3 py-2 rounded-lg border transition-colors bg-background hover:bg-accent/40"
                    >
                      {content}
                      <ExternalLink className="w-4 h-4 text-muted-foreground" />
                    </Link>
                  ) : (
                    <div
                      key={responsibleKey}
                      className="flex justify-between items-center px-3 py-2 rounded-lg border bg-background"
                    >
                      {content}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col gap-2 items-center">
                <span>
                  <CircleAlert className="text-primary" />
                </span>
                <p className="text-sm text-center text-muted-foreground">
                  {t('admin.user.profile.contacts.empty')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
